import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';

import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
} from './state';

export const useFilterConfig = (): FilterConfig => {
  const setFilterValues = useSetAtom(filterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const filterValues = useAtomValue(parsedFilterValuesAtom);

  useEffect(() => {
    // Only change if filter is empty or undefined
    if (!filterValues.filter) {
      setFilterValues((prevValues) => ({
        ...prevValues,
        filter: ['active'],
      }));
    }
  }, [setFilterValues]);

  // Initialize filter values from query string
  useEffect(() => {
    void setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {};

    void initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          void setFilterValues(queryFilters);
        }
      })
      .finally(() => {
        void setQuerystringFiltersInitialized(true);
      });

    return () => {
      void setQuerystringFiltersInitialized(false);
    };
  }, []);
  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: ['scanner_type', 'filter', 'operating_system'],
      leftPanelFilters: ['dateRangeFilters', 'ip'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'operating_system',
          label: 'Operating System',
          placeholder: 'Select OS',
          tooltip: (
            <div>
              <p>Filter hosts by Operating Systems (multi select).</p>
            </div>
          ),
          iconClass: 'fas fa-desktop',
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Windows', value: 'windows' },
            { label: 'Linux', value: 'linux' },
            { label: 'Unix', value: 'unix' },
            { label: 'MacOS', value: 'macos' },
            { label: 'Other', value: 'other', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new InputFilterConfig({
          id: 'ip',
          label: 'IP Address',
          tooltip: 'Filter by Host IP Address.',
          iconClass: 'fas fa-id-card',
          inputProps: {
            type: 'text',
            placeholder: 'Host IP Address',
            maxLength: 50,
          },
          debounceTime: 1000,
          enableDebounce: true,
        }),
        new MultiSelectFilterConfig({
          id: 'scanner_type',
          label: 'Scanner Type',
          tooltip: 'Filter by scanner type.',
          iconClass: 'fas fa-network-wired',
          defaultSelectedValues: [''],
          options: [
            { value: '', label: 'All', deselectOthers: true },
            { value: 'Internal', label: 'Internal', deselectOthers: true },
            { value: 'External', label: 'External', deselectOthers: true },
            { value: 'EC2', label: 'AWS', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'filter',
          label: 'Status',
          tooltip: 'Filter by cloud agent status.',
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            {
              value: '',
              label: 'All',
              deselectOthers: true,
            },
            {
              value: 'active',
              label: 'Active',
              deselectOthers: true,
            },
            {
              value: 'aging',
              label: 'Only In Aging Out Process',
              deselectOthers: true,
            },
            {
              value: 'deleted',
              label: 'Deleted (Aged Out or Manual)',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'updated_at',
              label: 'Modified Date',
              labelWidth: '115px',
            }),
            new DateRangeFilterConfig({
              id: 'last_vm_scanned_date',
              label: 'Last Scan Date',
              labelWidth: '115px',
            }),
          ],
        }),
      ],
    });
    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);
    return newConfig;
  }, [filterValues]);
  return config;
};
